import * as z from "zod";

const StorefrontClientScriptAttributesSchema = z.record(z.string(), z.any());
const StorefrontClientScriptFunnelSchema = z.object({
  id: z.number().int(),
  name: z.string().nullish(),
});

export const StorefrontScriptTargetingModeSchema = z.enum(["ALL", "INCLUDE_SELECTED", "EXCLUDE_SELECTED"]);
export type StorefrontScriptTargetingMode = z.infer<typeof StorefrontScriptTargetingModeSchema>;

export function escapeHtmlAttrValue(value: string) {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

export function normalizeStorefrontScriptAttributes(attributes: unknown): Record<string, unknown> | null {
  if (attributes == null) {
    return null;
  }

  if (typeof attributes === "string") {
    try {
      const parsed = JSON.parse(attributes);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }

  if (typeof attributes === "object" && !Array.isArray(attributes)) {
    return attributes as Record<string, unknown>;
  }

  return null;
}

export const StorefrontClientScriptFormSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    code: z.string().default(""),
    isTracking: z.boolean(),
    targetingMode: StorefrontScriptTargetingModeSchema.default("ALL"),
    funnelIds: z.array(z.number().int()).default([]),
  })
  .refine((value) => Boolean(value.code?.trim()), {
    message: "Provide script code.",
    path: ["code"],
  })
  .refine((value) => value.targetingMode !== "INCLUDE_SELECTED" || value.funnelIds.length > 0, {
    message: "Add at least one funnel.",
    path: ["funnelIds"],
  });

export const StorefrontClientScriptInputSchema = StorefrontClientScriptFormSchema;


export const StorefrontClientScriptSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  code: z.string().nullish(),
  src: z.string().nullish(),
  body: z.string().nullish(),
  isTracking: z.boolean().optional(),
  targetingMode: StorefrontScriptTargetingModeSchema.optional(),
  funnelIds: z.array(z.number().int()).optional(),
  funnels: z.array(StorefrontClientScriptFunnelSchema).optional(),
  createdAt: z.coerce.date().transform((val) => val.toISOString()),
  attributes: StorefrontClientScriptAttributesSchema.nullish(),
});
export type StorefrontClientScriptForm = z.infer<typeof StorefrontClientScriptFormSchema>;
export type StorefrontClientScript = z.infer<typeof StorefrontClientScriptSchema>;
